import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, entityAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ChefHat, 
  Phone, 
  ArrowRight, 
  ArrowLeft,
  User,
  ShoppingBag,
  Store,
  Shield,
  Loader2,
  AlertCircle
} from 'lucide-react';

// Admin phone number - must match backend
const ADMIN_PHONE = "9986378311";

const ROLES = [
  { id: 'buyer', label: 'Buyer', description: 'Order delicious homemade food', icon: ShoppingBag },
  { id: 'seller', label: 'Seller', description: 'Share your cooking with neighbors', icon: ChefHat },
  { id: 'dealer', label: 'Vendor', description: 'Sell products in communities', icon: Store },
  { id: 'admin', label: 'Admin', description: 'Manage the platform', icon: Shield, restricted: true },
];

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [step, setStep] = useState('phone'); // phone, otp, register
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState(location.state?.defaultRole || '');
  const [entityId, setEntityId] = useState('');
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEntities();
  }, []);

  const loadEntities = async () => {
    try {
      const response = await entityAPI.getAll();
      setEntities(response.data);
    } catch (error) {
      console.error('Failed to load entities:', error);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (mobile.length < 10) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    
    setLoading(true);
    try {
      await authAPI.sendOTP(mobile);
      toast.success('OTP sent successfully! (Use 121212 for testing)');
      setStep('otp');
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the complete OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await authAPI.verifyOTP(mobile, otp);
      
      if (response.data.is_new_user) {
        setStep('register');
      } else {
        login(response.data.user);
        toast.success('Welcome back!');
        navigateByRole(response.data.user.role);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!role) {
      toast.error('Please select a role');
      return;
    }
    
    // Admin restriction check
    if (role === 'admin' && mobile !== ADMIN_PHONE) {
      toast.error(`Admin registration is restricted to authorized phone numbers only.`);
      return;
    }
    
    if ((role === 'buyer' || role === 'seller' || role === 'dealer') && !entityId) {
      toast.error('Please select your apartment complex');
      return;
    }
    
    setLoading(true);
    try {
      const userData = {
        mobile,
        name: name.trim(),
        role,
        entity_id: entityId || null
      };
      
      const response = await authAPI.register(userData);
      login(response.data);
      toast.success('Registration successful! Welcome to RuchiBox!');
      navigateByRole(role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const navigateByRole = (userRole) => {
    switch (userRole) {
      case 'buyer':
        navigate('/buyer');
        break;
      case 'seller':
        navigate('/seller');
        break;
      case 'dealer':
        navigate('/dealer');
        break;
      case 'admin':
        navigate('/admin');
        break;
      default:
        navigate('/');
    }
  };

  const canSelectAdmin = mobile === ADMIN_PHONE;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4">
        <button 
          onClick={() => step === 'phone' ? navigate('/') : setStep('phone')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-none shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <ChefHat className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="font-heading text-2xl">
              {step === 'phone' && 'Welcome to RuchiBox'}
              {step === 'otp' && 'Verify OTP'}
              {step === 'register' && 'Complete Your Profile'}
            </CardTitle>
            <CardDescription>
              {step === 'phone' && 'Enter your mobile number to continue'}
              {step === 'otp' && `Enter the OTP sent to ${mobile}`}
              {step === 'register' && 'Tell us a bit about yourself'}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Phone Number Step */}
            {step === 'phone' && (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="mobile"
                      data-testid="mobile-input"
                      type="tel"
                      placeholder="Enter your mobile number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 h-12 text-lg"
                      maxLength={10}
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  data-testid="send-otp-btn"
                  className="w-full h-12 rounded-full text-lg font-semibold"
                  disabled={loading || mobile.length < 10}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Send OTP
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            )}

            {/* OTP Step */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-center block">Enter 6-digit OTP</Label>
                  <Input
                    data-testid="otp-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-14 text-2xl text-center font-mono tracking-[0.5em] border-2"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                  <p className="text-center text-sm text-muted-foreground">
                    Hint: Use <span className="font-mono font-bold text-primary">121212</span> for testing
                  </p>
                </div>
                
                <Button 
                  type="submit" 
                  data-testid="verify-otp-btn"
                  className="w-full h-12 rounded-full text-lg font-semibold"
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Verify & Continue
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
                
                <button 
                  type="button"
                  onClick={() => setStep('phone')}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
                >
                  Change mobile number
                </button>
              </form>
            )}

            {/* Registration Step */}
            {step === 'register' && (
              <form onSubmit={handleRegister} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="name"
                      data-testid="name-input"
                      type="text"
                      placeholder="Enter your full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>I am a...</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {ROLES.map((r) => {
                      const isAdminRole = r.id === 'admin';
                      const isDisabled = isAdminRole && !canSelectAdmin;
                      
                      return (
                        <button
                          key={r.id}
                          type="button"
                          data-testid={`role-${r.id}`}
                          onClick={() => !isDisabled && setRole(r.id)}
                          disabled={isDisabled}
                          className={`role-card p-4 text-left ${role === r.id ? 'selected' : ''} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <r.icon className={`w-6 h-6 mb-2 ${role === r.id ? 'text-primary' : 'text-muted-foreground'}`} />
                          <p className="font-semibold text-sm">{r.label}</p>
                          <p className="text-xs text-muted-foreground">{r.description}</p>
                          {isAdminRole && !canSelectAdmin && (
                            <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Restricted
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {(role === 'buyer' || role === 'seller' || role === 'dealer') && (
                  <div className="space-y-2">
                    <Label htmlFor="entity">Your Apartment Complex</Label>
                    <Select value={entityId} onValueChange={setEntityId}>
                      <SelectTrigger data-testid="entity-select" className="h-12">
                        <SelectValue placeholder="Select your society" />
                      </SelectTrigger>
                      <SelectContent>
                        {entities.map((entity) => (
                          <SelectItem key={entity.id} value={entity.id}>
                            {entity.name} - {entity.city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {entities.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No communities available yet. Contact admin.
                      </p>
                    )}
                  </div>
                )}

                <Button 
                  type="submit" 
                  data-testid="register-btn"
                  className="w-full h-12 rounded-full text-lg font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Complete Registration
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
